/**
 * Mutations for the issue surface.
 *
 * Two rules run through all of them:
 *
 *  1. The server owns derived state. A status change re-homes the card into
 *     that status's default column and stamps `resolved_at`; a label edit
 *     mirrors the names into `tasks.tags` so the iPad still sees them. So every
 *     mutation replaces the cached row with the row the server returned rather
 *     than merging its own guess on top.
 *  2. An optimistic write is always paired with a snapshot. On failure the
 *     snapshot goes back, because a card that silently stays where it was
 *     dropped is worse than one that visibly springs back.
 */

import { useCallback } from 'react'
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import type { UUID } from '@/types/api'

import { v2Api } from './api'
import { issueKeys, issueListKeys } from './keys'
import type {
  BacklogResponse,
  BoardResponse,
  BulkSetInput,
  Issue,
  IssueDetail,
  IssueListResponse,
  IssueWriteInput,
  MoveIssueInput,
} from './types'
import { issueRef } from './types'

/** Replace one issue everywhere it is currently cached. */
export function patchIssueInCaches(client: QueryClient, slug: string, issue: Issue): void {
  const swap = (list: Issue[]): Issue[] => {
    let changed = false
    const next = list.map((entry) => {
      if (entry.id !== issue.id) return entry
      changed = true
      return { ...entry, ...issue }
    })
    return changed ? next : list
  }

  client.setQueriesData<BoardResponse>({ queryKey: issueKeys.boards(slug) }, (board) => {
    if (!board) return board
    return { ...board, columns: board.columns.map((column) => ({ ...column, issues: swap(column.issues) })) }
  })

  client.setQueriesData<BacklogResponse>({ queryKey: issueKeys.backlogs(slug) }, (backlog) => {
    if (!backlog) return backlog
    return {
      ...backlog,
      sprints: backlog.sprints.map((group) => ({ ...group, issues: swap(group.issues) })),
      backlog: { ...backlog.backlog, issues: swap(backlog.backlog.issues) },
    }
  })

  client.setQueriesData<IssueListResponse>({ queryKey: issueKeys.issueLists(slug) }, (page) =>
    page ? { ...page, data: swap(page.data) } : page,
  )

  client.setQueryData<IssueDetail>(issueKeys.issue(slug, issueRef(issue)), (detail) =>
    detail ? { ...detail, ...issue } : detail,
  )
}

/** Invalidate every list that could contain the issue that just changed. */
export function invalidateIssueLists(client: QueryClient, slug: string): void {
  for (const key of issueListKeys(slug)) {
    void client.invalidateQueries({ queryKey: key })
  }
}

export function usePatchIssue(slug: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ ref, input }: { ref: string; input: IssueWriteInput }) =>
      v2Api.patchIssue(slug as string, ref, input),
    onSuccess: (issue, variables) => {
      if (!slug) return
      patchIssueInCaches(client, slug, issue)
      void client.invalidateQueries({ queryKey: issueKeys.issue(slug, variables.ref) })
      void client.invalidateQueries({ queryKey: issueKeys.activity(slug, variables.ref) })
      invalidateIssueLists(client, slug)
    },
  })
}

export function useCreateIssue(slug: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ projectKey, input }: { projectKey: string; input: IssueWriteInput }) =>
      v2Api.createIssue(slug as string, projectKey, input),
    onSuccess: () => {
      if (slug) invalidateIssueLists(client, slug)
    },
  })
}

export function useDeleteIssue(slug: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (ref: string) => v2Api.deleteIssue(slug as string, ref),
    onSuccess: (_data, ref) => {
      if (!slug) return
      client.removeQueries({ queryKey: issueKeys.issue(slug, ref) })
      invalidateIssueLists(client, slug)
    },
  })
}

export function useMoveIssue(slug: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ ref, input }: { ref: string; input: MoveIssueInput }) =>
      v2Api.moveIssue(slug as string, ref, input),
    onSuccess: (issue) => {
      if (slug) patchIssueInCaches(client, slug, issue)
    },
  })
}

export function useBulkIssues(slug: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { issue_keys: string[]; action?: 'update' | 'delete'; set?: BulkSetInput }) =>
      v2Api.bulkIssues(slug as string, input),
    onSuccess: () => {
      if (slug) invalidateIssueLists(client, slug)
    },
  })
}

export function useSetIssueLabels(slug: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ ref, labels }: { ref: string; labels: string[] }) =>
      v2Api.setIssueLabels(slug as string, ref, labels),
    onSuccess: (issue, variables) => {
      if (!slug) return
      patchIssueInCaches(client, slug, issue)
      void client.invalidateQueries({ queryKey: issueKeys.labels(slug) })
      void client.invalidateQueries({ queryKey: issueKeys.issue(slug, variables.ref) })
      invalidateIssueLists(client, slug)
    },
  })
}

export function useCommentMutations(slug: string | null, ref: string) {
  const client = useQueryClient()

  const refresh = useCallback(() => {
    if (!slug) return
    void client.invalidateQueries({ queryKey: issueKeys.comments(slug, ref) })
    void client.invalidateQueries({ queryKey: issueKeys.activity(slug, ref) })
    void client.invalidateQueries({ queryKey: issueKeys.issue(slug, ref) })
  }, [client, slug, ref])

  const create = useMutation({
    mutationFn: (body: string) => v2Api.createComment(slug as string, ref, body),
    onSuccess: refresh,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: UUID; body: string }) =>
      v2Api.patchComment(slug as string, id, body),
    onSuccess: refresh,
  })

  const remove = useMutation({
    mutationFn: (id: UUID) => v2Api.deleteComment(slug as string, id),
    onSuccess: refresh,
  })

  return { create, update, remove }
}
